import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { safeUrl } from '../lib/safeUrl';
import { companyLinkedinLinks, trackLinkedinClick } from '../lib/linkedin';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePlanGate, planGateFromResponse } from '../lib/planGate';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { buildCountryGate } from '../lib/countryGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import { STANDARD_INDUSTRIES, getIndustryLabel } from '../constants/industries';
import { getCountryOptions, getCountryName } from '../constants/countries';
import IndustryMultiSelectFilter from '../components/IndustryMultiSelectFilter';
import { COMPANY_ROLES, ROLE_COLORS, getRoleLabel } from '../constants/roles';
import AdminCompanyDialog from '../components/dialogs/AdminCompanyDialog';
import EntityCardActions from '../components/EntityCardActions';
import LinkedItemsPopover from '../components/LinkedItemsPopover';
import LinkedEntityFilter from '../components/LinkedEntityFilter';
import useFavorites from '../hooks/useFavorites';
import useListStateCache from '../hooks/useListStateCache';
import { trackSearch, trackFeature } from '../services/eventTracker';
import { toast } from 'sonner';
import { OnboardingEmptyState } from '../onboarding';
import { 
  Users, Search, Globe, Building, Briefcase, 
  ChevronLeft, ChevronRight, ExternalLink, Link2,
  RefreshCw, FileText, Building2, ArrowRight,
  ChevronDown, X, Factory, Plus, ArrowLeft, Download, FileJson, FileSpreadsheet, User, Heart, ThumbsUp, Newspaper,
  LayoutGrid, List, Linkedin
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

// Role icons mapping
const roleIcons = {
  developer: Building,
  contractor: Briefcase,
  consultant: Users,
  supplier: Building2,
  others: Building
};

// Multi-select filter component
const MultiSelectFilter = ({ label, icon: Icon, options, selected, onChange, language, isRTL, testId, searchable = false, onLockedClick }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = () => onChange([]);
  const filteredOptions = searchable && query
    ? options.filter(o => (o.label || '').toLowerCase().includes(query.toLowerCase()))
    : options;
  const selectAll = () => onChange(filteredOptions.map(o => o.value));

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="h-11 px-3 justify-between min-w-[160px]"
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[100px]">
              {selected.length === 0 
                ? label 
                : selected.length === 1 
                  ? options.find(o => o.value === selected[0])?.label || selected[0]
                  : `${selected.length} ${language === 'ar' ? 'محدد' : 'selected'}`
              }
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        {searchable && (
          <div className="p-2 border-b">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={language === 'ar' ? 'بحث...' : 'Search...'}
              className="h-9 bg-background"
              data-testid={`${testId}-search`}
            />
          </div>
        )}
        <div className="p-2 border-b flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={selectAll} className="h-7 text-xs">
            {language === 'ar' ? 'تحديد الكل' : 'Select All'}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">
            {language === 'ar' ? 'مسح' : 'Clear'}
          </Button>
        </div>
        <ScrollArea className="h-[250px]">
          <div className="p-2 space-y-1">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                {language === 'ar' ? 'لا توجد نتائج' : 'No results'}
              </div>
            ) : filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                  option.locked ? 'opacity-70' : selected.includes(option.value) ? 'bg-primary/10' : ''
                }`}
                onClick={() => (option.locked ? onLockedClick?.(option.value) : toggleOption(option.value))}
              >
                <Checkbox
                  checked={!option.locked && selected.includes(option.value)}
                  onCheckedChange={() => (option.locked ? onLockedClick?.(option.value) : toggleOption(option.value))}
                  disabled={option.locked}
                  className="pointer-events-none"
                />
                {option.icon && <span>{option.icon}</span>}
                <span className="text-sm flex-1">{option.label}</span>
                {option.locked && (
                  <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 whitespace-nowrap" data-testid={`locked-country-${option.value}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    {language === 'ar' ? 'ستاندرد' : 'Standard'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        {selected.length > 0 && (
          <div className="p-2 border-t">
            <div className="flex flex-wrap gap-1">
              {selected.slice(0, 3).map((val) => {
                const opt = options.find(o => o.value === val);
                return (
                  <Badge key={val} variant="secondary" className="text-xs">
                    {opt?.icon} {opt?.label?.slice(0, 10)}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer" 
                      onClick={(e) => { e.stopPropagation(); toggleOption(val); }}
                    />
                  </Badge>
                );
              })}
              {selected.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{selected.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default function CompaniesPage() {
  const { language, isRTL } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterIds = (searchParams.get('filter_ids') || '').split(',').filter(Boolean);
  const isFiltered = filterIds.length > 0;
  const isAdmin = user?.role === 'admin';
  const canCreateEntities = isAdmin || user?.plan_id === 'standard' || user?.plan_id === 'premium' || user?.plan_id === 'enterprise';
  
  const [companies, setCompanies] = useState([]);
  const [allIndustries, setAllIndustries] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('companies_view_mode') || 'card');
  useEffect(() => { localStorage.setItem('companies_view_mode', viewMode); }, [viewMode]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Admin CRUD state
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deleteCompany, setDeleteCompany] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Extract-as-Lead state
  const [savingLeadId, setSavingLeadId] = useState(null);
  const [savedLeadIds, setSavedLeadIds] = useState(() => new Set());
  
  // Multi-select Filters
  const [search, setSearch] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [linkedProjectFilter, setLinkedProjectFilter] = useState([]); // [{ _id, name }]
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  
  // Use the centralized favorites hook
  const { 
    isFavorite, 
    handleFavoriteToggle, 
    filterByFavorites,
    showFavoritesOnly, 
    setShowFavoritesOnly 
  } = useFavorites('company', token);
  
  // Page size
  const PAGE_SIZE = 20;

  // Check if user can export (not free plan)
  const canExport = user?.plan_id?.toLowerCase() !== 'free';
  const planGate = usePlanGate();
  const { allowedCountries, allCountries, upgradeUrl, planId, gccCountries } = useEntitlements();
  const isCountryLocked = (code) => !allCountries && !allowedCountries.includes(code);
  const handleLockedCountry = (code) => planGate.show(buildCountryGate(code, planId, upgradeUrl, language));
  const [countryGate, setCountryGate] = useState(null);

  // Export handler — server returns a 402 plan_gate when the plan is too low.
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/companies/export?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) return;
        let detail = '';
        try { detail = (await response.clone().json()).detail; } catch { /* non-json */ }
        throw new Error(detail || (language === 'ar' ? 'تعذّر التصدير' : 'Export could not be completed'));
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `companies_export.${format === 'excel' ? 'xlsx' : format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(language === 'ar' 
        ? `تم تصدير الشركات بنجاح (${format.toUpperCase()})`
        : `Companies exported successfully (${format.toUpperCase()})`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || (language === 'ar' ? 'فشل التصدير' : 'Export failed'));
    } finally {
      setIsExporting(false);
    }
  };

  const fetchCompanies = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const currentPage = isLoadMore ? page + 1 : 1;
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: PAGE_SIZE.toString()
      });
      
      // WO-73: every chosen country, role and industry goes to the server, which
      // filters the WHOLE database (before, the browser filtered only the 20
      // companies already loaded).
      selectedCountries.forEach(c => params.append('country', c));
      if (linkedProjectFilter.length > 0) linkedProjectFilter.forEach(f => params.append('project_id', f._id));
      selectedRoles.forEach(r => params.append('role', r));
      selectedIndustries.forEach(ind => params.append('industry', ind));
      if (search) params.append('search', search);
      
      const response = await fetch(`${BACKEND_URL}/api/intelligence/companies?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Failed to fetch companies');
      
      const data = await response.json();
      
      // Collect all unique industries from raw data (before filtering)
      const industries = [...new Set(data.items.flatMap(item => item.industries || []).filter(Boolean))];
      setAllIndustries(prev => {
        const combined = [...new Set([...prev, ...industries])];
        return combined.length > prev.length ? combined : prev;
      });
      
      // WO-73: the server already applied every filter.
      const filteredItems = data.items;
      
      if (isLoadMore) {
        setCompanies(prev => [...prev, ...filteredItems]);
        setPage(currentPage);
      } else {
        setCompanies(filteredItems);
        setPage(1);
      }
      
      setHasMore(data.page < data.total_pages);
      setTotal(data.total);
      setCountryGate(data.plan_gate || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const loadMore = () => {
    fetchCompanies(true);
  };

  useEffect(() => {
    if (isFiltered) return;
    if (lastLoadedSigRef.current === fetchSig) return; // already have data for these filters (e.g. just restored)
    lastLoadedSigRef.current = fetchSig;
    fetchCompanies(false);
  }, [isFiltered, selectedCountries, selectedRoles, selectedIndustries, search, linkedProjectFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isFiltered || !token) return;
    const loadFiltered = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          filterIds.map(id =>
            fetch(`${BACKEND_URL}/api/intelligence/companies/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(r => r.ok ? r.json() : null)
          )
        );
        const valid = results.filter(Boolean);
        setCompanies(valid);
        setTotal(valid.length);
      } finally {
        setLoading(false);
      }
    };
    loadFiltered();
  }, [filterIds.join(','), token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter companies by favorites if toggle is on
  const displayedCompanies = filterByFavorites(companies);

  // ── Restore loaded cards + filters + scroll when returning from a detail page ──
  const { restoredState, write } = useListStateCache('companies', '/dashboard/companies/');
  // Signature of the server-affecting filters (favorites is client-side only).
  const fetchSig = JSON.stringify({
    isFiltered, search, selectedCountries, selectedRoles, selectedIndustries, linkedProjectFilter,
  });
  // Tracks the filter signature the currently-loaded items belong to, so the
  // fetch effect can skip re-fetching when we've just restored from cache.
  const lastLoadedSigRef = useRef(restoredState ? restoredState.sig : null);

  // Restore filters + items BEFORE the fetch effect runs (layout effect).
  useLayoutEffect(() => {
    if (!restoredState) return;
    const f = restoredState.filters || {};
    setSearch(f.search || '');
    setSearchInput(f.searchInput || '');
    setSelectedCountries(f.selectedCountries || []);
    setSelectedRoles(f.selectedRoles || []);
    setSelectedIndustries(f.selectedIndustries || []);
    setLinkedProjectFilter(f.linkedProjectFilter || []);
    if (f.showFavoritesOnly) setShowFavoritesOnly(true);
    setCompanies(restoredState.items || []);
    setPage(restoredState.page || 1);
    setHasMore(!!restoredState.hasMore);
    setTotal(restoredState.total || 0);
    setLoading(false);
    const y = restoredState.scrollY || 0;
    setTimeout(() => window.scrollTo(0, y), 60);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist current list state (items + filters + scroll) when leaving the page.
  const latestStateRef = useRef({});
  latestStateRef.current = {
    items: companies, page, hasMore, total, sig: fetchSig, scrollY: 0,
    filters: {
      search, searchInput, selectedCountries, selectedRoles, selectedIndustries,
      linkedProjectFilter, showFavoritesOnly,
    },
  };
  // Persist on every change so returning from a detail page restores everything.
  useEffect(() => {
    write({ ...latestStateRef.current, scrollY: window.scrollY });
  }, [companies, page, hasMore, total, fetchSig, showFavoritesOnly, write]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearAllFilters = () => {
    setSelectedCountries([]);
    setSelectedRoles([]);
    setSelectedIndustries([]);
    setLinkedProjectFilter([]);
  };

  const hasActiveFilters = selectedCountries.length > 0 || selectedRoles.length > 0 || selectedIndustries.length > 0 || linkedProjectFilter.length > 0;

  // Admin handlers
  const handleCreateCompany = () => {
    setEditingCompany(null);
    setShowCompanyDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteCompany) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/companies/${deleteCompany._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete company');
      }
      
      toast.success(language === 'ar' ? 'تم حذف الشركة بنجاح' : 'Company deleted successfully');
      fetchCompanies(false);
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error(error.message);
    } finally {
      setIsDeleting(false);
      setDeleteCompany(null);
    }
  };

  const handleExtractAsLead = async (company) => {
    setSavingLeadId(company._id);
    try {
      const response = await fetch(`${BACKEND_URL}/api/opportunities/from-company/${company._id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to extract lead');

      setSavedLeadIds(prev => new Set(prev).add(company._id));
      if (data.already_in_crm) {
        toast.info(language === 'ar' ? 'هذه الشركة موجودة بالفعل في CRM الخاص بك' : 'This company is already in your CRM');
      } else {
        toast.success(language === 'ar' ? 'تمت إضافة الشركة إلى CRM الخاص بك كعميل محتمل' : 'Company added to your CRM as a lead');
        trackFeature('extract_lead', { entity: 'company' });
      }
    } catch (error) {
      console.error('Error extracting lead:', error);
      toast.error(error.message);
    } finally {
      setSavingLeadId(null);
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const translations = {
    en: {
      title: 'Companies',
      subtitle: 'Discover key players in GCC construction and infrastructure',
      searchPlaceholder: 'Search companies...',
      allCountries: 'All Countries',
      allRoles: 'All Roles',
      allIndustries: 'All Industries',
      developer: 'Developer',
      contractor: 'Contractor',
      consultant: 'Consultant',
      supplier: 'Supplier',
      others: 'Others',
      results: 'companies found',
      noCompanies: 'No companies found',
      noCompaniesDesc: 'Try adjusting your filters',
      viewProfile: 'View Profile',
      visitWebsite: 'Visit Website',
      news: 'news',
      projects: 'projects',
      tenders: 'tenders',
      refresh: 'Refresh',
      page: 'Page',
      of: 'of',
      activeFilters: 'Active filters:',
      clearAll: 'Clear all',
      createCompany: 'Create Company',
      editCompany: 'Edit',
      deleteCompany: 'Delete',
      deleteConfirmTitle: 'Delete Company?',
      deleteConfirmDesc: 'This action cannot be undone. This will permanently delete the company.',
      cancel: 'Cancel',
      deleting: 'Deleting...'
    },
    ar: {
      title: 'الشركات',
      subtitle: 'اكتشف اللاعبين الرئيسيين في قطاع البناء والبنية التحتية الخليجي',
      searchPlaceholder: 'البحث في الشركات...',
      allCountries: 'جميع الدول',
      allRoles: 'جميع الأدوار',
      allIndustries: 'جميع القطاعات',
      developer: 'مطور',
      contractor: 'مقاول',
      consultant: 'استشاري',
      supplier: 'مورد',
      others: 'أخرى',
      results: 'شركة',
      noCompanies: 'لا توجد شركات',
      noCompaniesDesc: 'حاول تعديل عوامل التصفية',
      viewProfile: 'عرض الملف',
      visitWebsite: 'زيارة الموقع',
      news: 'أخبار',
      projects: 'مشاريع',
      tenders: 'مناقصات',
      refresh: 'تحديث',
      page: 'صفحة',
      of: 'من',
      activeFilters: 'الفلاتر النشطة:',
      clearAll: 'مسح الكل',
      createCompany: 'إنشاء شركة',
      editCompany: 'تعديل',
      deleteCompany: 'حذف',
      deleteConfirmTitle: 'حذف الشركة؟',
      deleteConfirmDesc: 'لا يمكن التراجع عن هذا الإجراء. سيتم حذف الشركة نهائياً.',
      cancel: 'إلغاء',
      deleting: 'جارٍ الحذف...'
    }
  };

  const txt = translations[language];
  
  const roleOptions = [
    { value: 'developer', label: txt.developer },
    { value: 'contractor', label: txt.contractor },
    { value: 'consultant', label: txt.consultant },
    { value: 'supplier', label: txt.supplier },
    { value: 'others', label: txt.others }
  ];

  const CompanyCard = ({ company }) => {
    const primaryRole = company.roles?.[0];
    const RoleIcon = primaryRole ? roleIcons[primaryRole] : Building;
    const isFromCRM = company.source === 'crm_conversion' && company.visibility === 'private';
    const linkedinLinks = companyLinkedinLinks(company);
    const [isPremium, setIsPremium] = useState(
      company.is_premium || company.creator_plan === 'enterprise'
    );
    const [togglingPremium, setTogglingPremium] = useState(false);
    const [liked, setLiked] = useState(!!company.liked);
    const [likesCount, setLikesCount] = useState(company.likes_count || 0);
    const [likeBusy, setLikeBusy] = useState(false);

    const handleToggleLike = async (e) => {
      e.stopPropagation();
      if (likeBusy) return;
      setLikeBusy(true);
      const prevLiked = liked;
      const prevCount = likesCount;
      // optimistic update
      setLiked(!prevLiked);
      setLikesCount(prevCount + (prevLiked ? -1 : 1));
      try {
        const res = await fetch(`${BACKEND_URL}/api/intelligence/companies/${company._id}/like`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('like failed');
        const data = await res.json();
        setLiked(data.liked);
        setLikesCount(data.likes_count);
      } catch (err) {
        setLiked(prevLiked);
        setLikesCount(prevCount);
        toast.error(language === 'ar' ? 'فشل تحديث الإعجاب' : 'Failed to update like');
      } finally {
        setLikeBusy(false);
      }
    };

    const handleTogglePremium = async (e) => {
      e.stopPropagation();
      if (!isAdmin || togglingPremium) return;
      setTogglingPremium(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/companies/${company._id}/toggle-premium`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setIsPremium(data.is_premium);
        }
      } catch (err) {
        console.error('Toggle premium failed', err);
      } finally {
        setTogglingPremium(false);
      }
    };

    return (
      <Card
        className={`group relative border backdrop-blur-sm card-hover card-glow cursor-pointer flex flex-col ${
          isFromCRM
            ? 'border-amber-400/60 bg-gradient-to-br from-amber-50 to-amber-100/30 dark:from-amber-950/40 dark:to-amber-900/10 dark:border-amber-500/40 ring-1 ring-amber-300/40'
            : 'border-transparent bg-card/50'
        }`}
        data-testid={`company-card-${company._id}`}
        onClick={() => navigate(`/dashboard/companies/${company._id}`)}
      >
        {/* Hover highlight overlay */}
        <div className="card-hover-highlight" />

        {/* Premium star badge — top-right corner */}
        <button
          onClick={handleTogglePremium}
          disabled={!isAdmin || togglingPremium}
          className={`absolute top-3 right-3 z-20 p-1 rounded-full transition-all focus:outline-none ${
            isAdmin ? 'hover:scale-110 cursor-pointer' : 'cursor-default'
          } ${togglingPremium ? 'opacity-50' : ''}`}
          title={isAdmin ? (isPremium ? 'Remove premium status' : 'Mark as premium') : (isPremium ? 'Premium company' : '')}
          data-testid={`company-premium-star-${company._id}`}
        >
          <svg
            viewBox="0 0 24 24"
            className={`w-5 h-5 transition-colors ${
              isPremium
                ? 'fill-amber-400 stroke-amber-500'
                : 'fill-transparent stroke-gray-300 hover:stroke-amber-400'
            }`}
            strokeWidth="1.5"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </button>

        {/* WO-47 — LinkedIn icon next to the star; only for a company or showcase page.
            Arabic: mirrored to the top-left corner (the reference badge sits beside the star). */}
        {linkedinLinks && (
          <a
            href={linkedinLinks.page}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.stopPropagation(); trackLinkedinClick('company_linkedin_click', company, 'card'); }}
            className={`absolute z-20 p-1 rounded-full text-[#0A66C2] opacity-80 hover:opacity-100 hover:scale-110 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              isRTL ? 'top-1.5 left-1.5' : 'top-3 right-10'
            }`}
            title={language === 'ar' ? 'عرض على لينكدإن' : 'View on LinkedIn'}
            aria-label={language === 'ar' ? 'عرض على لينكدإن' : 'View on LinkedIn'}
            data-testid={`company-linkedin-icon-${company._id}`}
          >
            <Linkedin className="w-5 h-5" />
          </a>
        )}
        
        <CardContent className="p-6 flex flex-col relative z-10">
          {/* Header with Avatar - Fixed Height */}
          <div className={`flex items-start gap-4 mb-4 h-[70px] ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Avatar className="w-14 h-14 ring-2 ring-primary/20 shrink-0">
              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold text-lg">
                {getInitials(company.canonical_name)}
              </AvatarFallback>
            </Avatar>
            <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
              {/* Ref ID + From CRM Badges - inline with name */}
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                {company.ref_id && (
                  <Badge className="bg-primary text-primary-foreground font-mono text-xs" data-testid={`company-ref-id-${company._id}`}>
                    {company.ref_id}
                  </Badge>
                )}
                {isFromCRM && (
                  <Badge
                    className="bg-amber-500 hover:bg-amber-500 text-white font-medium text-[10px] gap-1"
                    data-testid={`company-from-crm-${company._id}`}
                    title={language === 'ar' ? 'تم إنشاؤها من CRM — مرئية لك فقط' : 'Created from CRM — visible only to you'}
                  >
                    {language === 'ar' ? 'من CRM' : 'From CRM'}
                  </Badge>
                )}
              </div>
              <h3 className="text-lg font-bold truncate group-hover:text-primary transition-colors">
                {company.canonical_name}
              </h3>
              {company.country && (
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <span>{countryFlags[company.country] || ''}</span>
                  <span>{getCountryName(company.country, language)}</span>
                </p>
              )}
            </div>
          </div>

          {/* Roles - Fixed Height */}
          <div className="flex flex-wrap gap-2 mb-4 h-[32px] overflow-hidden">
            {company.roles?.map((roleKey, i) => {
              const Icon = roleIcons[roleKey] || Building;
              return (
                <Badge key={i} className={`${ROLE_COLORS[roleKey] || 'bg-gray-500/10'} capitalize`}>
                  <Icon className="w-3 h-3 mr-1" />
                  {getRoleLabel(roleKey, language)}
                </Badge>
              );
            })}
          </div>

          {/* Stats - 3 counters in one row */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <LinkedItemsPopover ids={company.linked_news_ids} type="news" token={token}>
              <div className={`p-2.5 rounded-lg bg-muted/30 transition-colors ${company.linked_news_ids?.length ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <Newspaper className="w-3.5 h-3.5" />
                  <span className="text-xs">{txt.news}</span>
                </div>
                <p className={`text-xl font-bold ${company.linked_news_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{company.linked_news_ids?.length || 0}</p>
              </div>
            </LinkedItemsPopover>
            <LinkedItemsPopover ids={company.linked_tender_ids} type="tenders" token={token}>
              <div className={`p-2.5 rounded-lg bg-muted/30 transition-colors ${company.linked_tender_ids?.length ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="text-xs">{txt.tenders}</span>
                </div>
                <p className={`text-xl font-bold ${company.linked_tender_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{company.linked_tender_ids?.length || 0}</p>
              </div>
            </LinkedItemsPopover>
            <LinkedItemsPopover ids={company.linked_project_ids} type="projects" token={token}>
              <div className={`p-2.5 rounded-lg bg-muted/30 transition-colors ${company.linked_project_ids?.length ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <Building2 className="w-3.5 h-3.5" />
                  <span className="text-xs">{txt.projects}</span>
                </div>
                <p className={`text-xl font-bold ${company.linked_project_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{company.linked_project_ids?.length || 0}</p>
              </div>
            </LinkedItemsPopover>
          </div>

          {/* Spacer to push bottom content down */}
          <div className="flex-1"></div>

          {/* Created by + Likes */}
          <div className={`flex items-center justify-between gap-2 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 text-xs text-muted-foreground min-w-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate">
                {language === 'ar' ? 'أنشئ بواسطة:' : 'Created by:'} {company.created_by_name || (company.created_by === 'admin' ? 'Admin' : company.created_by) || 'Admin'}
              </span>
            </div>
            <button
              onClick={handleToggleLike}
              disabled={likeBusy}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium shrink-0 transition-all hover:scale-105 ${
                liked
                  ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
                  : 'bg-muted/40 text-muted-foreground hover:bg-muted'
              } ${likeBusy ? 'opacity-60' : ''}`}
              title={liked ? (language === 'ar' ? 'إلغاء الإعجاب' : 'Unlike') : (language === 'ar' ? 'إعجاب' : 'Like')}
              data-testid={`company-like-btn-${company._id}`}
            >
              <ThumbsUp className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
              <span data-testid={`company-like-count-${company._id}`}>{likesCount}</span>
            </button>
          </div>

          {/* Footer */}
          <div className={`flex items-center justify-between pt-4 border-t border-border/50 mt-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
            <EntityCardActions
              itemId={company._id}
              itemType="company"
              itemTitle={company.canonical_name}
              isAdmin={isAdmin}
              canEdit={isAdmin}
              canWatch={user?.plan_id !== 'free'}
              canSaveAsLead={true}
              onSaveAsLead={() => handleExtractAsLead(company)}
              isSavingLead={savingLeadId === company._id}
              savedAsLead={savedLeadIds.has(company._id)}
              onEdit={() => {
                setEditingCompany(company);
                setShowCompanyDialog(true);
              }}
              onDelete={(isAdmin || (company.created_by && user?.id && company.created_by === user.id)) ? () => setDeleteCompany(company) : undefined}
              isFavorite={isFavorite(company._id)}
              onFavoriteToggle={(isFav) => handleFavoriteToggle(company._id, isFav)}
              token={token}
              userPlanId={user?.plan_id}
              language={language}
              isRTL={isRTL}
              primaryAction={() => navigate(`/dashboard/companies/${company._id}`)}
              primaryLabel={txt.viewProfile}
              primaryIcon={ArrowRight}
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="companies-page">
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />
      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg">
              <Users className="w-7 h-7 text-white" />
            </div>
            {txt.title}
          </h1>
          <p className="text-muted-foreground mt-2">{txt.subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-wo40="header-actions">
          {/* Export Button - Only for paid users */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                disabled={isExporting}
                data-testid="export-companies-btn"
              >
                <Download className="w-4 h-4" />
                {isExporting 
                  ? (language === 'ar' ? 'جاري التصدير...' : 'Exporting...') 
                  : (language === 'ar' ? 'تصدير' : 'Export')}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
                <FileJson className="w-4 h-4 mr-2" />
                JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2" />
                CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel')} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {canCreateEntities && (
            <Button 
              onClick={handleCreateCompany}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90"
              data-testid="create-company-btn"
            >
              <Plus className="w-4 h-4" />
              {txt.createCompany}
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => fetchCompanies(false)}
            className="flex items-center gap-2"
            data-testid="refresh-companies-btn"
          >
            <RefreshCw className="w-4 h-4" />
            {txt.refresh}
          </Button>
          {/* View mode toggle */}
          <div className="flex items-center border rounded-md overflow-hidden">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-9 px-3"
              onClick={() => setViewMode('card')}
              data-testid="companies-view-card-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-9 px-3"
              onClick={() => setViewMode('list')}
              data-testid="companies-view-list-btn"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 bg-card/50 backdrop-blur-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className={`m2b-mfilters flex flex-col md:flex-row gap-3 flex-wrap ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className="m2b-keep relative flex-1 min-w-[200px]">
              <Search className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={txt.searchPlaceholder}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`${isRTL ? 'pr-10' : 'pl-10'} h-11`}
                data-testid="companies-search-input"
              />
            </div>
            {/* WO-40 — phones: filters fold away behind this button (CSS in index.css, .m2b-mfilters) */}
            <label className="m2b-keep m2b-ftoggle md:hidden flex items-center justify-between h-11 px-4 border rounded-lg bg-background text-sm font-medium cursor-pointer select-none" data-testid="companies-filters-toggle">
              <input type="checkbox" className="sr-only" aria-label={language === 'ar' ? 'إظهار عوامل التصفية' : 'Show filters'} />
              <span>{language === 'ar' ? 'عوامل التصفية' : 'Filters'}</span>
              <span className="m2b-ftoggle-arrow" aria-hidden="true">▾</span>
            </label>
            
            {/* Multi-select Country Filter */}
            <MultiSelectFilter
              label={txt.allCountries}
              icon={Globe}
              options={getCountryOptions(language, { gccFirst: true }).filter(c => gccCountries.includes(c.code)).map(c => ({
                value: c.code,
                label: c.name,
                icon: countryFlags[c.code],
                locked: isCountryLocked(c.code)
              }))}
              onLockedClick={handleLockedCountry}
              selected={selectedCountries}
              onChange={(values) => { setSelectedCountries(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="companies-country-filter"
              searchable
            />
            
            {/* Multi-select Role Filter */}
            <MultiSelectFilter
              label={txt.allRoles}
              icon={Briefcase}
              options={roleOptions}
              selected={selectedRoles}
              onChange={(values) => { setSelectedRoles(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="companies-role-filter"
            />
            
            {/* Multi-select Industry Filter (grouped by category) */}
            <IndustryMultiSelectFilter
              label={txt.allIndustries}
              icon={Factory}
              selected={selectedIndustries}
              onChange={(values) => { setSelectedIndustries(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="companies-industry-filter"
            />

            {/* Filter by linked Project */}
            <LinkedEntityFilter
              entityType="projects"
              selected={linkedProjectFilter}
              onChange={(val) => { setLinkedProjectFilter(val); setPage(1); }}
              language={language}
              token={token}
              testId="companies-project-filter"
            />
            
            {/* Favorites Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-background">
              <Heart className={`w-4 h-4 ${showFavoritesOnly ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
              <span className="text-sm whitespace-nowrap">
                {language === 'ar' ? 'المفضلة فقط' : 'Favorites only'}
              </span>
              <Switch
                checked={showFavoritesOnly}
                onCheckedChange={setShowFavoritesOnly}
                data-testid="companies-favorites-toggle"
              />
            </div>

            <Button type="submit" className="m2b-keep bg-primary hover:bg-primary/90 h-11" data-testid="companies-search-btn">
              <Search className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'بحث' : 'Search'}
            </Button>
          </form>
          
          {/* Active filters display */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50 flex-wrap">
              <span className="text-sm text-muted-foreground">{txt.activeFilters}</span>
              {selectedCountries.map(code => (
                <Badge key={code} variant="secondary" className="gap-1">
                  {countryFlags[code] || ''} {getCountryName(code, language)}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCountries(prev => prev.filter(c => c !== code))} />
                </Badge>
              ))}
              {selectedRoles.map(role => (
                <Badge key={role} variant="secondary" className="gap-1">
                  <Briefcase className="w-3 h-3" /> {txt[role]}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedRoles(prev => prev.filter(r => r !== role))} />
                </Badge>
              ))}
              {selectedIndustries.map(industry => (
                <Badge key={industry} variant="secondary" className="gap-1">
                  <Factory className="w-3 h-3" /> {getIndustryLabel(industry, language)}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedIndustries(prev => prev.filter(i => i !== industry))} />
                </Badge>
              ))}
              {linkedProjectFilter.map(f => (
                <Badge key={f._id} variant="secondary" className="gap-1 border-primary/30 bg-primary/10 text-primary">
                  <Building2 className="w-3 h-3" /> {f.name}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setLinkedProjectFilter(prev => prev.filter(x => x._id !== f._id))} />
                </Badge>
              ))}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-7">
                {txt.clearAll}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results count */}
      {!loading && (
        <div className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
          {language === 'ar' 
            ? `عرض ${displayedCompanies.length} من ${total} شركة${showFavoritesOnly ? ' (المفضلة)' : ''}` 
            : `Showing ${displayedCompanies.length} of ${total} companies${showFavoritesOnly ? ' (Favorites)' : ''}`
          }
        </div>
      )}

      {/* Linked filter banner */}
      {isFiltered && !loading && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm">
          <span className="font-medium text-primary">
            Showing {filterIds.length} linked compan{filterIds.length !== 1 ? 'ies' : 'y'}
          </span>
          <button
            onClick={() => navigate('/dashboard/companies')}
            className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            View all companies <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border-0 bg-card/50">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
                <Skeleton className="h-4 w-3/4" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-20" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="border-0 bg-destructive/10">
          <CardContent className="p-8 text-center">
            <p className="text-destructive">{error}</p>
            <Button onClick={() => fetchCompanies(false)} variant="outline" className="mt-4">
              {language === 'ar' ? 'إعادة المحاولة' : 'Try Again'}
            </Button>
          </CardContent>
        </Card>
      ) : displayedCompanies.length === 0 ? (
        <Card className="border-0 bg-card/50">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <OnboardingEmptyState kind="noResults" variant="inline" when={hasActiveFilters || !!search} onClear={() => { clearAllFilters(); setSearch(''); setSearchInput(''); }}>
            <>
            <h3 className="text-xl font-semibold mb-2">{txt.noCompanies}</h3>
            <p className="text-muted-foreground">{txt.noCompaniesDesc}</p>
            </>
            </OnboardingEmptyState>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        /* ─── List / Table view ─── */
        <Card className="border-0 bg-card/50 backdrop-blur-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="font-semibold">{language === 'ar' ? 'الشركة' : 'Company'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'الدولة' : 'Country'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'الأدوار' : 'Roles'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'الصناعات' : 'Industries'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'الموقع' : 'Website'}</TableHead>
                <TableHead className="font-semibold text-center">{language === 'ar' ? 'الروابط' : 'Links'}</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedCompanies.map((company) => (
                <TableRow
                  key={company._id}
                  className="cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => navigate(`/dashboard/companies/${company._id}`)}
                  data-testid={`company-list-row-${company._id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Building className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm flex items-center gap-1">
                          {company.canonical_name}
                          {(company.is_premium || company.creator_plan === 'enterprise') && (
                            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-amber-400 stroke-amber-500" strokeWidth="1.5">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                          )}
                        </div>
                        {company.description && <div className="text-xs text-muted-foreground line-clamp-1 max-w-[200px]">{company.description}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{countryFlags[company.country] || ''} {getCountryName(company.country, language)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[140px]">
                      {(company.roles || []).slice(0, 2).map(role => (
                        <span key={role} className="text-xs px-1.5 py-0.5 rounded bg-primary/8 text-primary border border-primary/20">{role}</span>
                      ))}
                      {(company.roles || []).length > 2 && <span className="text-xs text-muted-foreground">+{company.roles.length - 2}</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[160px]">
                      {(company.industries || []).slice(0, 2).map(ind => (
                        <span key={ind} className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{getIndustryLabel(ind, language)}</span>
                      ))}
                      {(company.industries || []).length > 2 && <span className="text-xs text-muted-foreground">+{company.industries.length - 2}</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {safeUrl(company.website) ? (
                      <a href={safeUrl(company.website)} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline truncate block max-w-[120px]"
                        onClick={e => e.stopPropagation()}>
                        {company.website.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-3 text-xs font-bold">
                      <span className={company.linked_news_ids?.length ? 'text-emerald-600' : 'text-blue-900'}>{company.linked_news_ids?.length || 0}</span>
                      <span className={company.linked_tender_ids?.length ? 'text-emerald-600' : 'text-blue-900'}>{company.linked_tender_ids?.length || 0}</span>
                      <span className={company.linked_project_ids?.length ? 'text-emerald-600' : 'text-blue-900'}>{company.linked_project_ids?.length || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <ChevronDown className="w-4 h-4 text-muted-foreground -rotate-90" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
          {displayedCompanies.map((company) => (
            <CompanyCard key={company._id} company={company} />
          ))}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && displayedCompanies.length > 0 && (
        <div className="flex justify-center pt-8">
          <Button
            variant="outline"
            size="lg"
            onClick={loadMore}
            disabled={loadingMore}
            className="min-w-[200px]"
            data-testid="load-more-companies"
          >
            {loadingMore ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'تحميل المزيد من الشركات' : 'Load More Companies'}
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* No more items message */}
      {!hasMore && displayedCompanies.length > 0 && (
        <div className="text-center pt-8 text-muted-foreground">
          <p>{language === 'ar' ? 'لا يوجد المزيد من الشركات' : 'No more companies to load'}</p>
        </div>
      )}
      
      {/* Admin Company Dialog */}
      <AdminCompanyDialog
        open={showCompanyDialog}
        onOpenChange={setShowCompanyDialog}
        company={editingCompany}
        token={token}
        language={language}
        onSuccess={() => fetchCompanies(false)}
        onPlanGate={(pg) => planGate.show(pg)}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCompany} onOpenChange={(open) => !open && setDeleteCompany(null)}>
        <AlertDialogContent data-testid="delete-company-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{txt.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {txt.deleteConfirmDesc}
              {deleteCompany && (
                <span className="block mt-2 font-semibold text-foreground">
                  "{deleteCompany.canonical_name}"
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} data-testid="cancel-delete-company">
              {txt.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-company"
            >
              {isDeleting ? txt.deleting : txt.deleteCompany}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
